// ── Card DTOs ──────────────────────────────────────────────────────────────────
//
// API contracts — what clients send (CardCreate/CardUpdate) and receive (Card).
// No Mongoose, no DB details. Those live in card.entity.ts.

import { z } from "zod";
import { Board } from "./board.type";
import { Agent } from "./agent.type";
import { Profile } from "./profile.type";

// ── CardStatus ─────────────────────────────────────────────────────────────────

export const CardStatus = {
  BACKLOG: "backlog",
  TODO: "todo",
  DOING: "doing",
  BLOCKED: "blocked",
  DONE: "done",
} as const;

export type CardStatus = (typeof CardStatus)[keyof typeof CardStatus];

// ── CardPriority ───────────────────────────────────────────────────────────────

export const CardPriority = {
  LOW: "low",
  NORMAL: "normal",
  MEDIUM: "medium",
  HIGH: "high",
  CRITICAL: "critical",
  URGENT: "urgent",
} as const;

export type CardPriority = (typeof CardPriority)[keyof typeof CardPriority];

// ── PartType ───────────────────────────────────────────────────────────────────

export const PartType = {
  TEXT: "text",
  FILE: "file",
  DATA: "data",
} as const;

export type PartType = (typeof PartType)[keyof typeof PartType];

// ── Parts ──────────────────────────────────────────────────────────────────────
// Zod schemas for card content parts — API validation and response contracts.
// The stored shape is mirrored by partSchema in card.entity.ts (Mongoose only).

// ── Response Part schemas — used in Card (output / toDto) ─────────────────────
// Content fields are optional to support field projection (e.g. ?fields=parts.type
// returns only the discriminant without the content object).

export const TextPart = z.object({
  type: z.literal(PartType.TEXT).describe("Part type discriminant"),
  text: z.string().optional().describe("Text content"),
  metadata: z.record(z.string(), z.unknown()).optional().describe("Part metadata"),
});

export const FilePart = z.object({
  type: z.literal(PartType.FILE).describe("Part type discriminant"),
  file: z
    .object({
      name: z.string().min(1).describe("File name"),
      mimeType: z.string().min(1).describe("File MIME type"),
      id: z.string().min(1).describe("File storage ID"),
      size: z.number().optional().describe("File size in bytes"),
      url: z.string().optional().describe("Signed download URL"), // Computed — NOT stored. Injected by enrichCard().
    })
    .optional(),
  metadata: z.record(z.string(), z.unknown()).optional().describe("Part metadata"),
});

export const DataPart = z.object({
  type: z.literal(PartType.DATA).describe("Part type discriminant"),
  data: z.record(z.string(), z.unknown()).optional().describe("Structured key-value data"),
  metadata: z.record(z.string(), z.unknown()).optional().describe("Part metadata"),
});

/** Full Part union (response) — TextPart | FilePart | DataPart. */
export const Part = z.discriminatedUnion("type", [TextPart, FilePart, DataPart]);

// ── Input Part schemas — used in CardCreate / CardUpdate ──────────────────────
// Content fields are required — callers must provide the full part payload.
// FilePart is intentionally excluded: files must be uploaded via
// POST /cards/:cardId/attachments, not inlined in the card body.

const TextPartInput = z.object({
  type: z.literal(PartType.TEXT).describe("Part type discriminant"),
  text: z.string().min(1).describe("Text content"),
  metadata: z.record(z.string(), z.unknown()).optional().describe("Part metadata"),
});

const DataPartInput = z.object({
  type: z.literal(PartType.DATA).describe("Part type discriminant"),
  data: z.record(z.string(), z.unknown()).describe("Structured key-value data"),
  metadata: z.record(z.string(), z.unknown()).optional().describe("Part metadata"),
});

export const CardInputPart = z.discriminatedUnion("type", [TextPartInput, DataPartInput]);

export type TextPart = z.infer<typeof TextPart>;
export type FilePart = z.infer<typeof FilePart>;
export type DataPart = z.infer<typeof DataPart>;
export type Part = z.infer<typeof Part>;
export type CardInputPart = z.infer<typeof CardInputPart>;

// ── Computed types ─────────────────────────────────────────────────────────────

/**
 * Resolved related card metadata — returned in GET /cards/:id responses.
 * Never stored in DB. Computed from related_card_ids by enrichCard().
 *
 * status values:
 *   active             — card exists and is not deleted
 *   recycled           — card is soft-deleted (in recycle bin)
 *   permanently_deleted — card was hard-deleted; only card_id is available
 */
export const RelatedCardResolved = z.object({
  card_id: z.string().min(1).describe("Related card ID"),
  board_id: z.string().optional().describe("Parent board ID — absent when permanently deleted"),
  board_name: z.string().optional().describe("Parent board name — absent when permanently deleted"),
  title: z.string().optional().describe("Related card title — absent when permanently deleted"),
  status: z.enum(["active", "recycled", "permanently_deleted"]).describe("Card lifecycle status"),
  permanent_deleted_at: z
    .string()
    .optional()
    .describe("Scheduled permanent deletion time — present when recycled"),
});

export type RelatedCardResolved = z.infer<typeof RelatedCardResolved>;

// ── Response DTO — Card ────────────────────────────────────────────────────────

export const Card = z.object({
  id: z.string().describe("Unique resource identifier"),
  team_id: z.string().min(1).describe("Owning team ID"),
  board_id: z.string().min(1).describe("Parent board ID"),
  owner_id: z.string().min(1).describe("Owner user ID"),
  agent_id: z.string().optional().describe("Assigned agent ID"),
  title: z.string().min(1).max(100).describe("Card title"),
  description: z.string().optional().describe("Card description"),
  status: z
    .enum(Object.values(CardStatus) as [string, ...string[]])
    .describe("Card workflow status"),
  column_key: z.string().min(1).describe("Current board column key"),
  priority: z
    .enum(Object.values(CardPriority) as [string, ...string[]])
    .optional()
    .describe("Card priority level"),
  result: z.string().optional().describe("Task result content"),
  tags: z.array(z.string().min(1)).optional().describe("Card tags"),
  member_ids: z.array(z.string().min(1)).optional().describe("Assigned member user IDs"),
  /** Stored field — IDs of related cards. Used internally by the service. */
  related_card_ids: z.array(z.string().min(1)).optional().describe("IDs of related cards"),
  /** Computed field — resolved related cards. Only present in GET /:id responses. */
  related_cards_resolved: z
    .array(RelatedCardResolved)
    .optional()
    .describe("Resolved related card metadata — includes board name and lifecycle status"),
  parts: z.array(Part).optional().describe("Structured content parts"),
  metadata: z.record(z.string(), z.unknown()).optional().describe("Arbitrary additional metadata"),
  enabled: z.boolean().optional().describe("Whether card is enabled"),
  reference_code: z.string().optional().describe("External reference code"),
  deleted_at: z.date().optional().describe("Soft-delete timestamp"),
  deleted_by: z.string().optional().describe("User who soft-deleted this card"),
  /** Computed — NOT stored. Injected by enrichCard(). = deleted_at + RECYCLE_BIN_RETENTION_DAYS. */
  permanent_deleted_at: z.string().optional().describe("Scheduled permanent deletion time"),
  created_at: z.date().describe("Creation timestamp"),
  updated_at: z.date().describe("Last update timestamp"),
  // ── Computed embed fields — present only when ?embed= is requested ──────────
  // All embed fields use .partial() so that ?fields= sub-field projection
  // (e.g. ?embed=board&fields=board.name) returns valid partial objects without
  // Zod validation errors for absent fields.
  /** NOT stored. Resolved inline by the repo aggregation pipeline when ?embed=board. */
  board: Board.partial().optional().describe("Embedded board — present only when ?embed=board"),
  /** NOT stored. Resolved inline by the repo aggregation pipeline when ?embed=agent. */
  agent: Agent.partial().optional().describe("Embedded agent — present only when ?embed=agent"),
  /** NOT stored. Resolved inline by the repo aggregation pipeline when ?embed=owner. */
  owner: Profile.partial()
    .optional()
    .describe("Embedded owner profile — present only when ?embed=owner"),
});

export type Card = z.infer<typeof Card>;

// ── Input DTOs ─────────────────────────────────────────────────────────────────

export const CardCreate = Card.omit({
  id: true,
  created_at: true,
  updated_at: true,
  deleted_at: true,
  deleted_by: true,
  permanent_deleted_at: true,
  related_cards_resolved: true,
  // embed fields — computed by the repo, never sent by the client
  board: true,
  agent: true,
  owner: true,
}).extend({
  // nullish = string | null | undefined — null clears the agent assignment
  agent_id: z.string().min(1).nullish(),
  /**
   * Only TextPart and DataPart allowed on create/update.
   * FilePart is managed exclusively via POST /cards/:cardId/attachments.
   */
  parts: z.array(CardInputPart).optional(),
});

export type CardCreate = z.infer<typeof CardCreate>;

export const CardUpdate = CardCreate.partial();
export type CardUpdate = z.infer<typeof CardUpdate>;

// ── Bulk input types ───────────────────────────────────────────────────────────

export const CardBulkUpdateItem = CardUpdate.extend({
  card_id: z.string().min(1).describe("Card ID to update"),
});
export type CardBulkUpdateItem = z.infer<typeof CardBulkUpdateItem>;

export const CardBulkMoveItem = z.object({
  card_id: z.string().min(1).describe("Card ID to move"),
  column_key: z.string().min(1).describe("Destination column key"),
});
export type CardBulkMoveItem = z.infer<typeof CardBulkMoveItem>;

export const CardBulkDeleteItem = z.object({
  card_id: z.string().min(1).describe("Card ID to delete"),
});
export type CardBulkDeleteItem = z.infer<typeof CardBulkDeleteItem>;

// ── Session sub-resource types ─────────────────────────────────────────────────
// Sessions are nested under Cards: /cards/:id/sessions

export const Session = z.object({
  id: z.string().describe("Unique resource identifier"),
  card_id: z.string().describe("Parent card ID"),
  created_by: z.string().describe("Creator user ID"),
  metadata: z.record(z.string(), z.unknown()).optional().describe("Session metadata"),
  created_at: z.coerce.date().describe("Creation timestamp"),
  updated_at: z.coerce.date().describe("Last update timestamp"),
});
export type Session = z.infer<typeof Session>;

export const SessionCreate = z.object({
  // card_id comes from URL path — NOT in body
  // created_by injected by route from x-actor header (actor.id ?? "system")
  metadata: z.record(z.string(), z.unknown()).optional().describe("Session metadata"),
});
export type SessionCreate = z.infer<typeof SessionCreate>;

export const SessionUpdate = z.object({
  // Only metadata is mutable on a session — must be non-empty
  metadata: z
    .record(z.string(), z.unknown())
    .refine((m) => Object.keys(m).length > 0, {
      message: "metadata must contain at least one field",
    })
    .describe("Session metadata (must have at least one field)"),
});
export type SessionUpdate = z.infer<typeof SessionUpdate>;

// ── SessionData ───────────────────────────────────────────────────────────────

export const SessionData = z.object({
  id: z.string().describe("Unique resource identifier"),
  session_id: z.string().describe("Parent session ID"),
  label: z.string().optional().describe("Optional data entry label"),
  data: z.record(z.string(), z.unknown()).describe("Structured session data"),
  updated_by: z.string().optional().describe("Last updater user ID"),
  created_at: z.coerce.date().describe("Creation timestamp"),
  updated_at: z.coerce.date().describe("Last update timestamp"),
});
export type SessionData = z.infer<typeof SessionData>;

export const SessionDataCreate = z.object({
  label: z.string().optional().describe("Optional data entry label"),
  data: z.record(z.string(), z.unknown()).describe("Structured session data"),
});
export type SessionDataCreate = z.infer<typeof SessionDataCreate>;

export const SessionDataBatchCreate = z.object({
  items: z.array(SessionDataCreate).min(1).describe("Batch of session data entries to create"),
});
export type SessionDataBatchCreate = z.infer<typeof SessionDataBatchCreate>;

export const SessionDataUpdate = z
  .object({
    label: z.string().optional().describe("Optional data entry label"),
    data: z.record(z.string(), z.unknown()).optional().describe("Structured session data"),
    updated_by: z.string().optional().describe("Updater user ID"),
  })
  .refine((d) => Object.values(d).some((v) => v !== undefined), {
    message: "At least one field must be provided",
  });
export type SessionDataUpdate = z.infer<typeof SessionDataUpdate>;
